import { DeleteOutlineRounded } from "@mui/icons-material";
import { Button, Divider, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import {
  ItemDescriptionField,
  MaterialSymbolIcon,
  MaterialSymbols,
  ShareButton,
  SignalRGroup,
  TaskItemContextProperty,
  TaskItemFeed,
  TaskItemProperties,
} from "src/components";
import { TaskItemPropertyType } from "src/constants";
import { useSession, useSignalR } from "src/hooks";
import { queryKeys, useDeleteTaskItem, useEditTaskItem, useEditTaskItemFocus, useGetTaskItemDetail } from "src/http";
import { DetailPageLayout } from "src/pages";
import { setTitlePrefix, useDestructiveConfirm } from "src/utilities";

interface UrlParams {
  taskItemGuid: string;
}

const TaskItemDetailsPage = () => {
  const { userId: sessionUserGuid } = useSession();
  const { taskItemGuid } = useParams<keyof UrlParams>() as UrlParams;
  const { isLoading, data: taskItem } = useGetTaskItemDetail(taskItemGuid);
  const { mutate: editTaskItem } = useEditTaskItem();
  const { mutate: editTaskItemFocus } = useEditTaskItemFocus();
  const { mutate: deleteTaskITem } = useDeleteTaskItem();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const confirm = useDestructiveConfirm();
  const signalR = useSignalR();
  const queryClient = useQueryClient();

  const userOwnership = taskItem?.owners.find((x) => x.user.userId === sessionUserGuid);

  useEffect(() => {
    signalR.joinGroup(SignalRGroup.TaskItem(taskItemGuid));
    return () => signalR.leaveGroup(SignalRGroup.TaskItem(taskItemGuid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskItemGuid]);

  useEffect(() => {
    const handleShouldRefetchTaskItem = (taskItemGuid: string) => {
      queryClient.invalidateQueries(queryKeys.taskItems.getTaskItemDetail(taskItemGuid));
    };

    signalR.onShouldRefetchTaskItem(handleShouldRefetchTaskItem);
    return () => signalR.offShouldRefetchTaskItem(handleShouldRefetchTaskItem);
  }, [queryClient, signalR]);

  useEffect(() => {
    setTitlePrefix(taskItem?.description);
  }, [taskItem?.description]);

  function handleDescriptionChanged(value: string) {
    if (taskItem === undefined) {
      return;
    }

    editTaskItem({
      taskItemGuid: taskItem.guid,
      description: value,
    });
  }

  async function handleDeleteClicked() {
    if (taskItem === undefined) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Delete Task",
      content: "Are you sure you want to delete this task?",
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    deleteTaskITem(
      {
        taskItemGuid: taskItemGuid,
      },
      {
        onSuccess: () => {
          navigate(-1);
          enqueueSnackbar(`Task has been deleted`);
        },
      },
    );
  }

  function handleFocusTaskClicked() {
    if (taskItem === undefined || userOwnership === undefined) {
      return;
    }

    editTaskItemFocus({
      taskItemGuid: taskItem.guid,
      isFocused: !userOwnership.isFocused,
      ownerGuid: sessionUserGuid,
    });
  }

  const shouldDisplayContextProperty =
    !isLoading &&
    taskItem &&
    (taskItem.context || taskItem.parentTaskItem || taskItem.parentIssue || taskItem.userCanEdit);

  return (
    <DetailPageLayout
      title="Task"
      actions={
        <Stack direction="row" spacing={1} divider={<Divider orientation="vertical" flexItem />}>
          <ShareButton variant="outlined" href={window.location.href} />
          {taskItem?.userCanEdit && (
            <Button variant="outlined" color="error" onClick={handleDeleteClicked} sx={{ minWidth: 18 }}>
              <DeleteOutlineRounded />
            </Button>
          )}
        </Stack>
      }
    >
      <Stack divider={<Divider />}>
        <Stack sx={{ px: { xs: 0.5, sm: 1 } }} divider={<Divider flexItem variant="dashed" />}>
          {shouldDisplayContextProperty && taskItem && (
            <TaskItemContextProperty taskItem={taskItem} isEditable={taskItem.userCanEdit} sx={{ py: 0.25 }} />
          )}

          <ItemDescriptionField
            isLoading={isLoading}
            isEditable={taskItem?.userCanEdit ?? false}
            value={taskItem?.description}
            onChange={handleDescriptionChanged}
            variant="h4"
            sx={{ py: 0.5 }}
          />

          {userOwnership && (
            <Button
              variant="tertiary"
              startIcon={<MaterialSymbolIcon symbol={MaterialSymbols.Target} />}
              onClick={handleFocusTaskClicked}
              sx={{ alignSelf: "start", my: 0.5 }}
            >
              {userOwnership.isFocused ? "Remove from Tasks in Focus" : "Add to Tasks in Focus"}
            </Button>
          )}
        </Stack>

        <TaskItemProperties
          taskItem={taskItem}
          isLoading={isLoading}
          propertyVisibility={{
            [TaskItemPropertyType.Notes]: true,
            [TaskItemPropertyType.Ownership]: true,
            [TaskItemPropertyType.Status]: true,
            [TaskItemPropertyType.DueDate]: true,
            [TaskItemPropertyType.Subtasks]: true,
          }}
        />

        <TaskItemFeed taskItemGuid={taskItemGuid} />
      </Stack>
    </DetailPageLayout>
  );
};

export { TaskItemDetailsPage };
